import React, { useState } from 'react';
import { Key, Plus, Copy, RefreshCw, Search, Check } from 'lucide-react';
import { useTokens, useCreateToken, useRevokeToken, useRotateToken } from '@/hooks/useApi';
import { copyToClipboard } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { useEnvironment } from '@/contexts/EnvironmentContext';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Segmented } from '@/components/ui/Segmented';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Notice } from '@/components/ui/Notice';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { checkbox, fieldControlSm, fieldLabel, fieldHint } from '@/components/ui/fieldStyles';
import { cn } from '@/lib/utils';

// Must match the gateway's key scopes (CreateTokenRequestSchema). Overpass is
// held back while its query surface is reviewed; resolve is not a key scope.
const SCOPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'route', label: 'Routing' },
  { value: 'render', label: 'Map tiles' },
  { value: 'geocode', label: 'Search and address checks' },
  { value: 'reverse', label: 'Reverse geocoding' },
  { value: 'convert', label: 'Location codes (Open Location Code, H3)' },
];

function formatDate(unixSeconds: number | null): string {
  if (!unixSeconds) return 'Never';
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

const SCOPE_LABEL: Record<string, string> = Object.fromEntries(SCOPE_OPTIONS.map((o) => [o.value, o.label]));
const STATUS_OPTIONS = [
  { value: 'all' as const, label: 'All' },
  { value: 'active' as const, label: 'Active' },
  { value: 'revoked' as const, label: 'Revoked' },
];

const APIKeysPage: React.FC = () => {
  const { environment } = useEnvironment();
  const { data: tokens, isLoading } = useTokens();
  const createToken = useCreateToken();
  const revokeToken = useRevokeToken();
  const rotateToken = useRotateToken();

  const [createOpen, setCreateOpen] = useState(false);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [rotateTarget, setRotateTarget] = useState<{ id: string; name: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'revoked'>('all');
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [revealedToken, setRevealedToken] = useState<{ name: string; token: string } | null>(null);

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['geocode']);
  const [keyEnvironment, setKeyEnvironment] = useState<'sandbox' | 'production'>(environment);

  const filteredTokens = (tokens ?? [])
    .filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((t) => statusFilter === 'all' || (statusFilter === 'active' ? t.is_active : !t.is_active));

  const toggleScope = (scope: string) => {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  };

  const resetForm = () => {
    setName('');
    setScopes(['geocode']);
    setKeyEnvironment(environment);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await createToken.mutateAsync({ name, scopes, environment: keyEnvironment });
      setRevealedToken({ name: result.name, token: result.token });
      resetForm();
    } catch {
      setAlertMessage({ type: 'error', message: 'Failed to create key' });
    }
  };

  const handleRevoke = async () => {
    if (!revokeId) return;
    try {
      await revokeToken.mutateAsync(revokeId);
      setRevokeId(null);
      setAlertMessage({ type: 'success', message: 'Key revoked' });
    } catch {
      setAlertMessage({ type: 'error', message: 'Failed to revoke key' });
    }
  };

  const handleRotate = async () => {
    if (!rotateTarget) return;
    try {
      const result = await rotateToken.mutateAsync(rotateTarget.id);
      setRevealedToken({ name: rotateTarget.name, token: result.token });
      setRotateTarget(null);
    } catch {
      setAlertMessage({ type: 'error', message: 'Failed to rotate key' });
    }
  };

  const handleCopy = async (token: string) => {
    const success = await copyToClipboard(token);
    setAlertMessage(
      success ? { type: 'success', message: 'Copied to clipboard' } : { type: 'error', message: 'Failed to copy' }
    );
  };

  const hasKeys = !!tokens && tokens.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="API keys"
        description="Create and manage the keys your apps use to call Latlens."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
            Generate API key
          </Button>
        }
      />

      <Panel bodyClassName="p-0 pb-2">
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search keys…"
              aria-label="Search keys"
              className={cn(fieldControlSm, 'pl-9')}
            />
          </div>
          <Segmented label="Status" value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />
        </div>
        {isLoading ? (
          <TableSkeleton rows={3} className="pt-3" />
        ) : filteredTokens.length === 0 ? (
          <EmptyState
            icon={<Key />}
            title={hasKeys ? 'No matching keys' : 'No API keys yet'}
            description={hasKeys ? 'Try a different search or filter.' : 'Generate your first key to start calling the Latlens API. Sandbox keys are free.'}
            action={!hasKeys && (
              <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
                Generate API key
              </Button>
            )}
          />
        ) : (
          <Table minWidth={820}>
            <THead>
              <tr>
                <Th>Name</Th>
                <Th>Key</Th>
                <Th>Environment</Th>
                <Th>Scopes</Th>
                <Th>Status</Th>
                <Th>Last used</Th>
                <Th><span className="sr-only">Actions</span></Th>
              </tr>
            </THead>
            <TBody>
              {filteredTokens.map((token) => (
                <Tr key={token.id}>
                  <Td className="font-medium">{token.name}</Td>
                  <Td mono muted className="text-xs">{token.token_prefix}…</Td>
                  <Td>
                    <Badge tone={token.environment === 'production' ? 'accent' : 'neutral'}>
                      {token.environment === 'production' ? 'Production' : 'Sandbox'}
                    </Badge>
                  </Td>
                  <Td muted className="max-w-[260px]">
                    <span className="line-clamp-2" title={token.scopes.map((sc) => SCOPE_LABEL[sc] ?? sc).join(', ')}>{token.scopes.join(', ')}</span>
                  </Td>
                  <Td>
                    <Badge tone={token.is_active ? 'success' : 'neutral'} dot>{token.is_active ? 'Active' : 'Revoked'}</Badge>
                  </Td>
                  <Td muted className="whitespace-nowrap">{formatDate(token.last_used_at)}</Td>
                  <Td align="right">
                    {token.is_active && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-8 w-8 px-0" onClick={() => setRotateTarget({ id: token.id, name: token.name })} title="Rotate key" aria-label="Rotate key">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 px-0 hover:text-error" onClick={() => setRevokeId(token.id)} title="Revoke key" aria-label="Revoke key">
                          <Key className="h-4 w-4 text-error" />
                        </Button>
                      </div>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Generate API key" size="md">
        <form onSubmit={handleCreate} className="space-y-5">
          <Input label="Key name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Production backend" required />

          <div>
            <p className={fieldLabel}>Environment</p>
            <div className="flex gap-2">
              {(['sandbox', 'production'] as const).map((env) => (
                <button
                  key={env}
                  type="button"
                  onClick={() => setKeyEnvironment(env)}
                  aria-pressed={keyEnvironment === env}
                  className={`h-10 flex-1 rounded-xl border px-3 text-sm font-medium capitalize transition-colors ${
                    keyEnvironment === env
                      ? 'border-accent/50 bg-accent/10 text-accent-text'
                      : 'border-border text-muted-foreground hover:bg-surface-raised hover:text-foreground'
                  }`}
                >
                  {env}
                </button>
              ))}
            </div>
            <p className={fieldHint}>
              Sandbox calls never bill or count against your quota.
            </p>
          </div>

          <div>
            <p className={fieldLabel}>Endpoint scope</p>
            <div className="space-y-1 rounded-xl border border-border p-2">
              {SCOPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-foreground hover:bg-surface-raised">
                  <input
                    type="checkbox"
                    checked={scopes.includes(opt.value)}
                    onChange={() => toggleScope(opt.value)}
                    className={checkbox}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={createToken.isLoading} disabled={!name.trim() || scopes.length === 0}>
              Generate key
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!revealedToken}
        onClose={() => { setRevealedToken(null); setCreateOpen(false); }}
        title="Your new API key"
        size="md"
        closeOnOverlayClick={false}
      >
        {revealedToken && (
          <div className="space-y-4">
            <Notice tone="warning" title="Copy this key now. You won't be able to see it again." />
            <code className="block break-all rounded-xl border border-border bg-surface p-3 font-mono text-sm text-foreground">
              {revealedToken.token}
            </code>
            <div className="flex justify-end gap-3">
              <Button variant="outline" leftIcon={<Copy className="h-4 w-4" />} onClick={() => handleCopy(revealedToken.token)}>
                Copy key
              </Button>
              <Button leftIcon={<Check className="h-4 w-4" />} onClick={() => { setRevealedToken(null); setCreateOpen(false); }}>
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!rotateTarget} onClose={() => setRotateTarget(null)} title="Rotate API key" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            This immediately invalidates the current key value. Any application still using it will stop working until it's updated with the new value.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRotateTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRotate} loading={rotateToken.isLoading}>
              Rotate key
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!revokeId} onClose={() => setRevokeId(null)} title="Revoke API key" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Any application using this key will immediately stop being able to call the API. This can't be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRevokeId(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRevoke} loading={revokeToken.isLoading}>
              Revoke key
            </Button>
          </div>
        </div>
      </Modal>

      {alertMessage && (
        <Alert
          type={alertMessage.type}
          title={alertMessage.type === 'success' ? 'Success' : 'Error'}
          message={alertMessage.message}
          isOpen={!!alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </div>
  );
};

export default APIKeysPage;
